import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import Ably from "ably";
import { toast } from "react-toastify";

import { useAuthSelector } from "../../services/selectors/authSelector";
import { apiKey } from "../../networking/urlEndPoints";
import {
	acceptDriverHandoverRequest,
	createDriverHandoverRequest,
	dropDriverRoutes,
	fetchDriverActiveRoutes,
	fetchDriverHandoverPatients,
	fetchDriverHandoverRequests,
	fetchDriverHandoverTargets,
	fetchDriverHandoverUntempered,
	fetchDriverLogoutReadiness,
	fetchDriverRouteOptions,
	rejectDriverHandoverRequest,
	recordDriverActivitySignal,
	selectDriverRoutes,
} from "../../services/apis/dispense/routeAudit";
import {
	buildHandoverTargetOptions,
	getSelectedRoutesFromOptions,
	sortRouteOptions,
} from "../../utils/helper/routeAuditUi";

import * as Icons from "../../assets/svg/icons";
import { HANDOVER_MODE, TAB } from "./routeControlSheet/constants";
import { HandoverSignatureModal } from "./handoverSignature";

const LazyRoutesTab = lazy(() => import("./routeControlSheet/RoutesTab"));
const LazyLogoutTab = lazy(() => import("./routeControlSheet/LogoutTab"));
const LazyHandoverTab = lazy(() => import("./routeControlSheet/HandoverTab"));
const LazyRequestsTab = lazy(() => import("./routeControlSheet/RequestsTab"));

const TabIcon = ({ tab, active }) => {
	const className = `h-4 w-4 ${active ? "text-inherit" : "text-gray-500"}`;
	if (tab === TAB.HANDOVER) {
		return (
			<svg
				className={className}
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<path d="m11 17 2 2a1 1 0 1 0 3-3" />
				<path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
				<path d="m21 3 1 11h-2" />
				<path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
				<path d="M3 4h8" />
			</svg>
		);
	}
	if (tab === TAB.ROUTES) {
		return (
			<svg
				className={className}
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
				<circle cx="12" cy="10" r="3" />
			</svg>
		);
	}
	if (tab === TAB.REQUESTS) {
		return (
			<svg
				className={className}
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
				<path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
			</svg>
		);
	}
	if (tab === TAB.LOGOUT) {
		return (
			<svg
				className={className}
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<path d="m16 17 5-5-5-5" />
				<path d="M21 12H9" />
				<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
			</svg>
		);
	}
	return null;
};

const TabButton = ({ tab, label, activeTab, onClick, badge }) => {
	const isActive = activeTab === tab;
	return (
		<button
			type="button"
			onClick={onClick}
			className={`relative flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-2xl transition whitespace-nowrap ${
				isActive
					? "bg-indigo-100 text-indigo-700 shadow-sm"
					: "text-gray-600 hover:text-gray-900 hover:bg-slate-50"
			}`}
		>
			<TabIcon tab={tab} active={isActive} />
			<span>{label}</span>
			{badge ? (
				<span className="ml-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white">
					{badge > 9 ? "9+" : badge}
				</span>
			) : null}
		</button>
	);
};

const realtimeEventTypes = new Set([
	"delivery_handover_request",
	"delivery_handover_response",
	"patient_spotted_notification",
	"delivery_live_location",
	"delivery_live_location_request",
	"instore_duplicate_stock_return_updated",
]);
const routeRefreshAuditEventTypes = new Set([
	"route_timeout_expired",
	"route_ownership_released",
]);

const resolveRouteSelectionErrorMessage = (error, fallback) =>
	error?.response?.data?.data?.error?.original_delivery_personnel_signature?.[0]
	|| error?.response?.data?.data?.error?.routes?.[0]
	|| error?.response?.data?.message
	|| fallback;

let sharedRouteControlRealtime = null;

const getSharedRouteControlRealtime = () => {
	if (!apiKey) {
		return null;
	}

	const state = `${sharedRouteControlRealtime?.connection?.state ?? ""}`.toLowerCase();
	if (!sharedRouteControlRealtime || state === "closed" || state === "failed") {
		sharedRouteControlRealtime = new Ably.Realtime({ key: apiKey });
	}

	return sharedRouteControlRealtime;
};

const RouteTableSkeleton = () => (
	<div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
		{Array.from({ length: 8 }).map((_, index) => (
			<div key={index} className="min-h-[80px] animate-pulse rounded-xl border border-gray-200 bg-white px-2 py-2">
				<div className="h-6 w-10 rounded bg-gray-200" />
				<div className="mt-2 h-3 w-20 rounded bg-gray-100" />
				<div className="mt-1.5 h-3 w-16 rounded bg-gray-100" />
			</div>
		))}
	</div>
);

const RouteControlSummarySkeleton = () => (
	<div className="space-y-3">
		<div className="rounded-2xl border border-gray-200 bg-white p-3 animate-pulse">
			<div className="h-4 w-28 rounded bg-gray-200" />
			<div className="mt-3 grid grid-cols-2 gap-2">
				<div className="h-14 rounded-2xl bg-gray-100" />
				<div className="h-14 rounded-2xl bg-gray-100" />
			</div>
		</div>
		<div className="rounded-2xl border border-gray-200 bg-white p-3 animate-pulse">
			<div className="h-4 w-36 rounded bg-gray-200" />
			<div className="mt-3 h-14 rounded-2xl bg-gray-100" />
			<div className="mt-2 h-14 rounded-2xl bg-gray-100" />
		</div>
		<div className="rounded-2xl border border-gray-200 bg-white p-3 animate-pulse">
			<div className="h-4 w-28 rounded bg-gray-200" />
			<div className="mt-3 h-16 rounded-2xl bg-gray-100" />
		</div>
	</div>
);

export const RouteControlSheet = ({ isOpen, onClose, onPendingRequestsChange, onPickRoute, requestedTab = null, initialHandoverRoute = '' }) => {
	const { pharmacyId, selectedPharmacistID, driverID } = useAuthSelector();
	const isDirectRouteHandover = Boolean(initialHandoverRoute);
	const [routeOptions, setRouteOptions] = useState([]);
	const [activeRoutes, setActiveRoutes] = useState([]);
	const [logoutReadiness, setLogoutReadiness] = useState(null);
	const [handoverTargets, setHandoverTargets] = useState({ drivers: [], assistants: [], supervisors: [], pharmacists: [], instore_pharmacist: [] });
	const [handoverPatients, setHandoverPatients] = useState([]);
	const [pendingRequests, setPendingRequests] = useState([]);
	const [pendingRequestRoutes, setPendingRequestRoutes] = useState([]);
	const [selectedRoutes, setSelectedRoutes] = useState([]);
	const [selectedRouteForHandover, setSelectedRouteForHandover] = useState("");
	const [selectedPatientIdsForHandover, setSelectedPatientIdsForHandover] = useState([]);
	const [handoverUntempered, setHandoverUntempered] = useState([]);
	const [selectedUntemperedForHandover, setSelectedUntemperedForHandover] = useState("");
	const [handoverTargetType, setHandoverTargetType] = useState("");
	const [handoverMode, setHandoverMode] = useState(HANDOVER_MODE.ROUTE);
	const [assistantRecipientType, setAssistantRecipientType] = useState("teammate");
	const [assistantTeammateTarget, setAssistantTeammateTarget] = useState("");
	const [assistantPharmacistTarget, setAssistantPharmacistTarget] = useState("");
	const [message, setMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [activeTab, setActiveTab] = useState(TAB.HANDOVER);
	const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
	const [isRouteSignatureModalOpen, setIsRouteSignatureModalOpen] = useState(false);
	const hasAutoDroppedRef = useRef(false);
	const refreshDataRef = useRef(null);
	const loadTabDataRef = useRef(null);
	const activeTabRef = useRef(TAB.HANDOVER);

	// Notify parent of pending request count changes
	useEffect(() => {
		onPendingRequestsChange?.(pendingRequests.length);
	}, [pendingRequests, onPendingRequestsChange]);

	useEffect(() => {
		if (!message && !errorMessage) {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			setMessage("");
			setErrorMessage("");
		}, 3000);

		return () => window.clearTimeout(timeoutId);
	}, [message, errorMessage]);

	// const handoverTargetGroups = useMemo(() => {
	// 	const targetGroups = buildHandoverTargetOptions(handoverTargets);
	// 	const normalizedSelectedPharmacistId = Number(selectedPharmacistID);

	// 	if (!normalizedSelectedPharmacistId) {
	// 		return targetGroups;
	// 	}

	// 	return {
	// 		...targetGroups,
	// 		assistantPharmacists: targetGroups.assistantPharmacists.filter((target) => {
	// 			const linkedPharmacistId = Number(target?.linkedPharmacistId ?? target?.id);
	// 			return !linkedPharmacistId || linkedPharmacistId !== normalizedSelectedPharmacistId;
	// 		}),
	// 	};
	// }, [handoverTargets, selectedPharmacistID]);
	

	const handoverTargetGroups = useMemo(
		() => buildHandoverTargetOptions(handoverTargets),
		[handoverTargets]
	);

	const selectedTarget = useMemo(() => {
		if (handoverTargetType === "assistant") {
			return assistantRecipientType === "pharmacist"
				? assistantPharmacistTarget
				: assistantTeammateTarget;
		}

		if (handoverTargetType === "instore") {
			return "__instore_all__";
		}

		return "";
	}, [
		assistantPharmacistTarget,
		assistantRecipientType,
		assistantTeammateTarget,
		handoverTargetType,
	]);
	const visibleHandoverTargets = useMemo(
		() => {
			if (handoverTargetType === "assistant") {
				return assistantRecipientType === "pharmacist"
					? handoverTargetGroups.assistantPharmacists
					: handoverTargetGroups.teammates;
			}

			return handoverTargetGroups.pharmacists;
		},
		[
			assistantRecipientType,
			handoverTargetGroups.assistantPharmacists,
			handoverTargetGroups.pharmacists,
			handoverTargetGroups.teammates,
			handoverTargetType,
		]
	);
	const setSelectedTarget = useCallback((value) => {
		if (handoverTargetType === "assistant") {
			if (assistantRecipientType === "pharmacist") {
				setAssistantPharmacistTarget(value);
				return;
			}

			setAssistantTeammateTarget(value);
			return;
		}

		if (handoverTargetType === "instore") {
			return;
		}
	}, [assistantRecipientType, handoverTargetType]);
	// useEffect(() => {
	// 	if (!selectedTarget) {
	// 		return;
	// 	}

	// 	const selectedStillVisible = visibleHandoverTargets.some((target) => target.value === selectedTarget);
	// 	if (!selectedStillVisible) {
	// 		setSelectedTarget("");
	// 	}
	// }, [selectedTarget, setSelectedTarget, visibleHandoverTargets]);
	const pendingRequestRouteSet = useMemo(() => new Set(pendingRequestRoutes), [pendingRequestRoutes]);
	const selectedPatientOptions = useMemo(() => {
		const selectedIds = new Set(selectedPatientIdsForHandover.map(String));
		return handoverPatients.filter((patient) => selectedIds.has(String(patient?.patient_id ?? patient?.id)));
	}, [handoverPatients, selectedPatientIdsForHandover]);
	const selectedPatientOption = selectedPatientOptions[0] || null;

	useEffect(() => {
		const availablePatientIds = new Set(handoverPatients.map((patient) => String(patient?.patient_id ?? patient?.id)));
		setSelectedPatientIdsForHandover((selectedIds) => selectedIds.filter((id) => availablePatientIds.has(String(id))));
	}, [handoverPatients]);
	const selectedUntemperedOption = useMemo(
		() => handoverUntempered.find((item) => String(item?.patient_id) === String(selectedUntemperedForHandover)) || null,
		[handoverUntempered, selectedUntemperedForHandover]
	);
	const selectedRouteCount = selectedRoutes.length;
	const hasRouteSelectionChanges = useMemo(() => {
		const currentSelection = [...selectedRoutes].sort();
		const persistedSelection = getSelectedRoutesFromOptions(routeOptions).sort();

		if (currentSelection.length !== persistedSelection.length) {
			return true;
		}

		return currentSelection.some((route, index) => route !== persistedSelection[index]);
	}, [routeOptions, selectedRoutes]);

	// Keep activeTabRef in sync for use inside Ably handler (avoids stale closure)
	useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

	// Load data only for the given tab — avoids fetching all tabs on every open
	// silent=true: skip loading indicator / error state (used for background Ably-triggered refreshes)
	const loadTabData = useCallback(async (tab, { silent = false } = {}) => {
		if (!pharmacyId) return;
		if (!silent) {
			setLoading(true);
			setErrorMessage("");
		}
		try {
			if (tab === TAB.ROUTES) {
				const nextRouteOptions = await fetchDriverRouteOptions(pharmacyId);
				const sortedRouteOptions = sortRouteOptions(nextRouteOptions || []);
				setRouteOptions(sortedRouteOptions);
				setSelectedRoutes(getSelectedRoutesFromOptions(sortedRouteOptions));
			} else if (tab === TAB.LOGOUT) {
				const readiness = await fetchDriverLogoutReadiness(pharmacyId);
				setLogoutReadiness(readiness || null);
			} else if (tab === TAB.HANDOVER) {
				const [activeRouteData, targets, patients, untempered] = await Promise.all([
					fetchDriverActiveRoutes(pharmacyId),
					fetchDriverHandoverTargets(pharmacyId),
					fetchDriverHandoverPatients(pharmacyId),
					fetchDriverHandoverUntempered(pharmacyId),
				]);
				const fetchedPendingRoutes = targets?.pending_request_routes || [];
				setActiveRoutes(activeRouteData || []);
				setHandoverTargets(targets || { drivers: [], assistants: [], supervisors: [], pharmacists: [], instore_pharmacist: [] });
				setHandoverPatients(patients || []);
				setHandoverUntempered(untempered || []);
				setPendingRequestRoutes(fetchedPendingRoutes);
				setSelectedRouteForHandover((currentRoute) => {
					const routes = activeRouteData || [];
					if (initialHandoverRoute && routes.some((route) => route?.route === initialHandoverRoute)) {
						return initialHandoverRoute;
					}
					// Auto-select the only active route (keeps existing valid selection).
					if (!currentRoute && routes.length === 1 && routes[0]?.route) {
						return routes[0].route;
					}
					if (currentRoute === "__all__" && routes.length > 1) {
						return currentRoute;
					}
					if (currentRoute && routes.some((r) => r.route === currentRoute)) {
						return currentRoute;
					}
					return "";
				});
				setSelectedPatientIdsForHandover((currentPatientIds) => {
					const availablePatientIds = new Set(
						(patients || []).map((patient) => String(patient?.patient_id ?? patient?.id))
					);

					return currentPatientIds.filter((patientId) => availablePatientIds.has(String(patientId)));
				});
				setSelectedUntemperedForHandover((currentPatientId) =>
					(untempered || []).some((item) => String(item?.patient_id) === String(currentPatientId))
						? currentPatientId
						: ""
				);
			} else if (tab === TAB.REQUESTS) {
				const requests = await fetchDriverHandoverRequests(pharmacyId);
				setPendingRequests(requests || []);
			}
		} catch (error) {
			if (!silent) setErrorMessage(error?.response?.data?.message || "Unable to load route controls.");
		} finally {
			if (!silent) setLoading(false);
		}
	}, [initialHandoverRoute, pharmacyId]);

	// Silently refresh badge count only (no loading indicator).
	// Logout readiness is fetched on demand when the user opens that tab.
	const refreshBackground = useCallback(() => {
		if (!pharmacyId) return;
		fetchDriverHandoverRequests(pharmacyId)
			.then((requests) => setPendingRequests(requests || []))
			.catch(() => {});
	}, [pharmacyId]);

	// Keep refs in sync so Ably handler always uses the latest functions
	useEffect(() => {
		loadTabDataRef.current = loadTabData;
		refreshDataRef.current = () => {
			loadTabData(activeTabRef.current);
			refreshBackground();
		};
	}, [loadTabData, refreshBackground]);

	useEffect(() => {
		if (!isOpen || !requestedTab || requestedTab === activeTab) {
			return;
		}

		setActiveTab(requestedTab);
		loadTabData(requestedTab);
	}, [activeTab, isOpen, loadTabData, requestedTab]);

	// On open: load only the active tab's data, then silently refresh badge + logout
	useEffect(() => {
		if (!isOpen) {
			hasAutoDroppedRef.current = false;
			setSelectedRouteForHandover("");
			setSelectedPatientIdsForHandover([]);
			setSelectedUntemperedForHandover("");
			setHandoverMode(HANDOVER_MODE.ROUTE);
			setIsRouteSignatureModalOpen(false);
			return;
		}
		const nextTab = requestedTab || activeTab;
		if (nextTab !== activeTab) {
			setActiveTab(nextTab);
		}
		loadTabData(nextTab);
		refreshBackground();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, pharmacyId, requestedTab]);

	useEffect(() => {
		if (handoverMode === HANDOVER_MODE.UNTEMPERED) {
			setHandoverTargetType("instore");
			return;
		}

		if (handoverTargetType === "instore" && !selectedTarget) {
			setHandoverTargetType("");
		}
	}, [handoverMode, handoverTargetType, selectedTarget]);

	// Auto-drop routes with 0 pending patients when logout readiness loads
	useEffect(() => {
		if (!isOpen || !pharmacyId || !logoutReadiness || hasAutoDroppedRef.current) return;

		const zeroRoutes = (logoutReadiness.routes || []).filter((r) => (r.pending_patient_count ?? r.pending_rx_count) === 0);
		if (zeroRoutes.length === 0) return;

		hasAutoDroppedRef.current = true;
		Promise.all(
			zeroRoutes.map((r) => dropDriverRoutes({ pharmacyId, routes: [r.route] }).catch(() => {}))
		).then(() => loadTabData(activeTabRef.current));
	}, [isOpen, pharmacyId, logoutReadiness, loadTabData]);

	useEffect(() => {
		if (!isOpen || !pharmacyId || !apiKey) {
			return undefined;
		}

		const ably = getSharedRouteControlRealtime();
		if (!ably) {
			return undefined;
		}
		const channel = ably.channels.get("pharmacy-notifications");
		const eventName = `pharmacy-${pharmacyId}`;
		const handler = (messageEvent) => {
			const eventData = messageEvent?.data;
			const isRouteRefreshEvent = eventData?.type === "audit_trail_updated"
				&& routeRefreshAuditEventTypes.has(eventData?.event_type);

			if (!realtimeEventTypes.has(eventData?.type) && !isRouteRefreshEvent) {
				return;
			}

			// Issue 3: Notify requester when their handover request is accepted or rejected,
			// then immediately refresh HANDOVER tab data so "(request pending)" clears in real-time.
			if (eventData.type === "delivery_handover_response") {
				const requestedById = eventData?.requested_by_driver_id;
				if (requestedById && Number(requestedById) === Number(driverID)) {
					// Notification is shown globally from App.js. Here we only refresh HANDOVER tab data.
					const isOnHandoverTab = activeTabRef.current === TAB.HANDOVER;
					loadTabDataRef.current?.(TAB.HANDOVER, { silent: !isOnHandoverTab });
					refreshBackground();
					return;
				}
			}

			if (
				eventData.type === "delivery_handover_request" &&
				Number(eventData?.requested_by?.id) === Number(driverID)
			) {
				return;
			}

			// Issue 4: Notify assigned driver when a patient is spotted
			if (eventData.type === "patient_spotted_notification") {
				const targetDriverId = eventData?.target_driver_id;
				if (targetDriverId && Number(targetDriverId) === Number(driverID)) {
					const patientName = eventData?.patient_name
						|| [eventData?.patient?.first_name, eventData?.patient?.last_name].filter(Boolean).join(" ")
						|| "a patient";
					const spottedBy = eventData?.spotted_by_name || eventData?.seen_by?.name || "another driver";
					const route = eventData?.route || eventData?.patient?.route;
					const routeLabel = route ? ` on route ${route}` : "";
					toast.info(`Patient ${patientName} was spotted by ${spottedBy}${routeLabel}.`, { autoClose: 8000 });
				}
			}

			refreshDataRef.current?.();
		};

		channel.subscribe(eventName, handler);
		return () => {
			channel.unsubscribe(eventName, handler);
		};
	}, [isOpen, pharmacyId, driverID]);

	// Switch tab and lazily load its data
	const handleTabChange = useCallback((tab) => {
		setActiveTab(tab);
		loadTabData(tab);
	}, [loadTabData]);

	const handleToggleRoute = (routeOption) => {
		if (routeOption?.is_locked || routeOption?.is_selected) {
			return;
		}

		const route = routeOption?.route;
		if (!route) {
			return;
		}

		setSelectedRoutes((currentRoutes) =>
			currentRoutes.includes(route)
				? currentRoutes.filter((value) => value !== route)
				: [...currentRoutes, route]
		);
	};

	const handleClearSelectedRoutes = () => {
		setSelectedRoutes([]);
		setErrorMessage("");
	};

	const handleSaveRoutes = () => {
		if (!pharmacyId || selectedRoutes.length === 0) {
			setErrorMessage("Select at least one route.");
			return;
		}

		setMessage("");
		setErrorMessage("");
		setIsRouteSignatureModalOpen(true);
	};

	const handleConfirmRouteSignature = async (originalDeliveryPersonnelSignature) => {
		if (!originalDeliveryPersonnelSignature) {
			setErrorMessage(" Signature is required.");
			return false;
		}

		setSaving(true);
		setMessage("");
		setErrorMessage("");
		try {
			const result = await selectDriverRoutes({
				pharmacyId,
				routes: selectedRoutes,
				pharmacistId: selectedPharmacistID,
				originalDeliveryPersonnelSignature,
			});

			if (result?.conflicts?.length) {
				setErrorMessage(`${result.conflicts.map((item) => item.route).join(", ")} already picked by another user.`);
			} else {
				setMessage("Routes updated successfully.");
			}
			recordDriverActivitySignal({
				pharmacyId,
				route: selectedRoutes.length === 1 ? selectedRoutes[0] : undefined,
			});
			await loadTabData(activeTab);
			setIsRouteSignatureModalOpen(false);
			return true;
		} catch (error) {
			setErrorMessage(resolveRouteSelectionErrorMessage(error, "Unable to save routes."));
			setIsRouteSignatureModalOpen(false);
			return false;
		} finally {
			setSaving(false);
		}
	};

	const handleDropRoute = async (route) => {
		setSaving(true);
		setMessage("");
		setErrorMessage("");
		try {
			await dropDriverRoutes({ pharmacyId, routes: [route] });
			setMessage(`Route ${route} dropped.`);
			recordDriverActivitySignal({ pharmacyId });
			await loadTabData(activeTab);
		} catch (error) {
			const blockingMessage = error?.response?.data?.data?.error?.routes?.[0];
			setSelectedRouteForHandover(route);
			setErrorMessage(blockingMessage || error?.response?.data?.message || "Unable to drop route.");
		} finally {
			setSaving(false);
		}
	};

	const handleCreateHandover = async (signatureBase64) => {
		const isInstoreHandover = handoverTargetType === "instore";
		if (!handoverTargetType || !handoverMode || (!isInstoreHandover && !selectedTarget)) {
			setErrorMessage("Select a handover tab, handover mode, and receiving user.");
			return false;
		}

		if (!signatureBase64) {
			setErrorMessage("Signature is required before you can send a handover request.");
			return false;
		}

		const [targetType, rawTargetId] = isInstoreHandover
			? ["pharmacy_user", ""]
			: selectedTarget.split(":");
		const normalizedTargetId = isInstoreHandover ? null : Number(rawTargetId);
		setSaving(true);
		setMessage("");
		setErrorMessage("");
		try {
			if (handoverMode === HANDOVER_MODE.ROUTE) {
				if (!selectedRouteForHandover) {
					setErrorMessage("Select a route to hand over.");
					return false;
				}

				const isAll = selectedRouteForHandover === "__all__";
				const routesToSend = isAll ? activeRoutes.map((route) => route.route) : [selectedRouteForHandover];
				if (routesToSend.length === 0) {
					setErrorMessage("No active routes available to hand over.");
					return false;
				}

				const routeRequestPayload = {
					pharmacyId,
					targetType,
					targetId: normalizedTargetId,
					sendToAllInstore: isInstoreHandover,
					handoverType: HANDOVER_MODE.ROUTE,
					routes: routesToSend,
					signatureBase64,
				};

				await createDriverHandoverRequest(routeRequestPayload);
				recordDriverActivitySignal({
					pharmacyId,
					route: routesToSend.length === 1 ? routesToSend[0] : undefined,
				});
				setPendingRequestRoutes((currentRoutes) => Array.from(new Set([
					...(Array.isArray(currentRoutes) ? currentRoutes : []),
					...routesToSend,
				])));

				const label = isAll ? "all routes" : `route ${selectedRouteForHandover}`;
				setMessage(`Handover request sent for ${label}.`);
			} else if (handoverMode === HANDOVER_MODE.PATIENT) {
				if (selectedPatientOptions.length === 0) {
					setErrorMessage("Select at least one patient to hand over.");
					return false;
				}
				const selectedPatientIds = selectedPatientOptions.map((patient) => Number(patient.patient_id ?? patient.id));

				const patientRequestPayload = {
					pharmacyId,
					targetType,
					targetId: normalizedTargetId,
					sendToAllInstore: isInstoreHandover,
					handoverType: HANDOVER_MODE.PATIENT,
					patientId: selectedPatientIds.length === 1 ? selectedPatientIds[0] : undefined,
					patientIds: selectedPatientIds,
					signatureBase64,
				};

				await createDriverHandoverRequest(patientRequestPayload);
				recordDriverActivitySignal({
					pharmacyId,
					route: selectedPatientOptions.length === 1 ? `${selectedPatientOption?.route ?? ""}`.trim() || undefined : undefined,
				});
				setHandoverPatients((currentPatients) => (
					Array.isArray(currentPatients)
						? currentPatients.map((patient) => (
							selectedPatientIds.includes(Number(patient?.patient_id ?? patient?.id))
								? { ...patient, has_pending_request: true }
								: patient
						))
						: currentPatients
				));

				setSelectedPatientIdsForHandover([]);
				setMessage(`Handover request sent for ${selectedPatientIds.length === 1 ? selectedPatientOption?.patient_name || "the selected patient" : `${selectedPatientIds.length} patients`}.`);
			} else if (handoverMode === HANDOVER_MODE.UNTEMPERED) {
				if (!selectedUntemperedOption) {
					setErrorMessage("Select untempered items to hand over.");
					return false;
				}

				const untemperedRequestPayload = {
					pharmacyId,
					targetType: "pharmacy_user",
					targetId: null,
					sendToAllInstore: true,
					handoverType: HANDOVER_MODE.UNTEMPERED,
					patientId: Number(selectedUntemperedOption.patient_id),
					deliveryUnconsumedDoseIds: selectedUntemperedOption.delivery_unconsumed_dose_ids || [],
					signatureBase64,
				};

				await createDriverHandoverRequest(untemperedRequestPayload);
				recordDriverActivitySignal({
					pharmacyId,
					route: `${selectedUntemperedOption?.route ?? ""}`.trim() || undefined,
				});
				setHandoverUntempered((currentItems) => (
					Array.isArray(currentItems)
						? currentItems.map((item) => (
							String(item?.patient_id) === String(selectedUntemperedOption.patient_id)
								? { ...item, has_pending_request: true }
								: item
						))
						: currentItems
				));

				setMessage(`Untempered items sent for ${selectedUntemperedOption.patient_name || "the selected patient"}.`);
			}
			return true;
		} catch (error) {
			setErrorMessage(error?.response?.data?.data?.error?.handover?.[0] || error?.response?.data?.message || "Unable to send handover request.");
			return false;
		} finally {
			setSaving(false);
		}
	};

	const handleRespond = async (requestId, action, signatureBase64 = "") => {
		setSaving(true);
		setMessage("");
		setErrorMessage("");
		try {
			if (action === "accept") {
				if (!signatureBase64) {
					throw new Error("Signature is required before accepting a handover request.");
				}
				await acceptDriverHandoverRequest(requestId, signatureBase64);
				recordDriverActivitySignal({ pharmacyId });
				setMessage("Handover accepted.");
			} else if (action === "reject") {
				await rejectDriverHandoverRequest(requestId);
				recordDriverActivitySignal({ pharmacyId });
				setMessage("Handover rejected.");
			} else {
				throw new Error(`Unknown handover action: ${action}`);
			}
			await loadTabData(activeTab);
			return true;
		} catch (error) {
			setErrorMessage(error?.response?.data?.data?.error?.handover?.[0] || error?.response?.data?.message || "Unable to update handover request.");
			return false;
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
		{typeof document !== "undefined" ? createPortal(
			<AnimatePresence>
				{isOpen ? (
					<motion.div
						className="fixed inset-0 z-[1200]"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
					>
						<button
							type="button"
							aria-label="Close route controls"
							onClick={onClose}
							className="absolute inset-0 h-full w-full cursor-default bg-slate-950/25"
						/>
						<motion.section
							role="dialog"
							aria-modal="true"
							aria-label={isDirectRouteHandover ? `Hand over route ${initialHandoverRoute}` : "Route Controls"}
							className="absolute bottom-0 flex h-screen max-h-screen w-full min-h-0 flex-col bg-white [height:100dvh] [max-height:100dvh]"
							initial={{ y: "100%" }}
							animate={{ y: 0 }}
							exit={{ y: "100%" }}
							transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
						>
							<div className="flex justify-center pb-1 pt-3">
								<span className="h-1 w-9 rounded-full bg-slate-300" />
							</div>
							<div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
						{/* Header */}
						<div className="flex items-center justify-between border-b border-dashed px-4 py-3">
							<div>
								<h3 className="text-sm font-bold text-gray-900">
									{isDirectRouteHandover ? `Hand over route ${initialHandoverRoute}` : "Route Controls"}
								</h3>
							</div>
							<button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-900">
								<Icons.CloseModelIcon />
							</button>
						</div>

						{/* Today Dispense opens a focused route-handover flow. */}
						{!isDirectRouteHandover ? (
							<div className="border-b border-gray-200 bg-white px-3 py-2">
								<div className="flex gap-2 overflow-x-auto">
									<TabButton
										tab={TAB.HANDOVER}
										label="Handover"
										activeTab={activeTab}
										onClick={() => handleTabChange(TAB.HANDOVER)}
									/>
									<TabButton
										tab={TAB.ROUTES}
										label="Selected Routes"
										activeTab={activeTab}
										onClick={() => handleTabChange(TAB.ROUTES)}
									/>
									<TabButton
										tab={TAB.REQUESTS}
										label="Incoming"
										activeTab={activeTab}
										onClick={() => handleTabChange(TAB.REQUESTS)}
										badge={pendingRequests.length || 0}
									/>
									<TabButton
										tab={TAB.LOGOUT}
										label="Logout Readiness"
										activeTab={activeTab}
										onClick={() => handleTabChange(TAB.LOGOUT)}
									/>
								</div>
							</div>
						) : null}

						{/* Tab Content */}
						<div className="dashboard-route-control-scroller min-h-0 flex-1 overflow-y-auto bg-slate-50 px-2 py-2">
							{activeTab !== TAB.HANDOVER && message ? <div className="mx-1 mb-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{message}</div> : null}
							{activeTab !== TAB.HANDOVER && errorMessage ? <div className="mx-1 mb-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMessage}</div> : null}
							{loading ? (
								<div className="space-y-3">
									<div className="rounded-2xl border border-gray-200 bg-white p-3">
										<div className="mb-2 flex items-center justify-between">
											<div className="h-4 w-28 rounded bg-gray-200 animate-pulse" />
											<div className="h-3 w-12 rounded bg-gray-100 animate-pulse" />
										</div>
										<RouteTableSkeleton />
									</div>
									<RouteControlSummarySkeleton />
								</div>
							) : (
								<Suspense fallback={<RouteControlSummarySkeleton />}>
									{activeTab === TAB.ROUTES && (
										<LazyRoutesTab
											routeOptions={routeOptions}
											selectedRoutes={selectedRoutes}
											selectedRouteCount={selectedRouteCount}
											hasRouteSelectionChanges={hasRouteSelectionChanges}
											saving={saving}
											isSignatureModalOpen={isRouteSignatureModalOpen}
											loadTabData={loadTabData}
											activeTab={activeTab}
											handleToggleRoute={handleToggleRoute}
											handleClearSelectedRoutes={handleClearSelectedRoutes}
											handleSaveRoutes={handleSaveRoutes}
											onPickRoute={onPickRoute}
											onClose={onClose}
										/>
									)}
									{activeTab === TAB.LOGOUT && (
										<LazyLogoutTab
											logoutReadiness={logoutReadiness}
											saving={saving}
											handleTabChange={handleTabChange}
											setSelectedRouteForHandover={setSelectedRouteForHandover}
											setHandoverMode={setHandoverMode}
											handleDropRoute={handleDropRoute}
										/>
									)}
									{activeTab === TAB.HANDOVER && (
									<LazyHandoverTab
											handoverTargetType={handoverTargetType}
											setHandoverTargetType={setHandoverTargetType}
											handoverMode={handoverMode}
											setHandoverMode={setHandoverMode}
											activeRoutes={activeRoutes}
											selectedRouteForHandover={selectedRouteForHandover}
											setSelectedRouteForHandover={setSelectedRouteForHandover}
											handoverPatients={handoverPatients}
											selectedPatientIdsForHandover={selectedPatientIdsForHandover}
											setSelectedPatientIdsForHandover={setSelectedPatientIdsForHandover}
											handoverUntempered={handoverUntempered}
											selectedUntemperedForHandover={selectedUntemperedForHandover}
											setSelectedUntemperedForHandover={setSelectedUntemperedForHandover}
											assistantRecipientType={assistantRecipientType}
											setAssistantRecipientType={setAssistantRecipientType}
											visibleHandoverTargets={visibleHandoverTargets}
											selectedTarget={selectedTarget}
											setSelectedTarget={setSelectedTarget}
											pendingRequestRouteSet={pendingRequestRouteSet}
											selectedPatientOptions={selectedPatientOptions}
											selectedUntemperedOption={selectedUntemperedOption}
											handleCreateHandover={handleCreateHandover}
											saving={saving}
											message={message}
											errorMessage={errorMessage}
											onSignatureModalToggle={setIsSignatureModalOpen}
										/>
									)}
									{activeTab === TAB.REQUESTS && (
										<LazyRequestsTab
											pendingRequests={pendingRequests}
											handleRespond={handleRespond}
											saving={saving}
											onSignatureModalToggle={setIsSignatureModalOpen}
										/>
									)}
								</Suspense>
							)}
						</div>
					</div>
						</motion.section>
					</motion.div>
				) : null}
			</AnimatePresence>,
			document.body
		) : null}
		<HandoverSignatureModal
			isOpen={isRouteSignatureModalOpen}
			title=""
			description="Sign below to save the selected pickup routes."
			confirmLabel="Save Routes"
			confirmButtonClassName="bg-slate-900 hover:bg-slate-800"
			onCancel={() => setIsRouteSignatureModalOpen(false)}
			onConfirm={handleConfirmRouteSignature}
			loading={saving && isRouteSignatureModalOpen}
		/>
		</>
	);
};
